import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

export const StyledAnalyticsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media ${({ theme }) => theme.media.maxSm} {
    padding: 1rem;
  }
`;

export const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.media.maxMd} {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

export const StyledTitle = styled.h1`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media ${({ theme }) => theme.media.maxMd} {
    font-size: 2rem;
  }

  @media ${({ theme }) => theme.media.maxSm} {
    font-size: 1.75rem;
  }
`;

export const StyledPeriodSelector = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}10`};
  }
`;

export const StyledStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.media.maxSm} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

export const StyledStatCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media ${({ theme }) => theme.media.maxSm} {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

export const StyledStatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media ${({ theme }) => theme.media.maxSm} {
    width: 40px;
    height: 40px;
  }
`;

export const StyledStatInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StyledStatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

export const StyledStatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media ${({ theme }) => theme.media.maxSm} {
    font-size: 1.25rem;
  }
`;

export const StyledChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.media.maxLg} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media ${({ theme }) => theme.media.maxSm} {
    gap: 1rem;
  }
`;

export const StyledChartCard = styled.div`
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.backgroundSecondary};

  @media ${({ theme }) => theme.media.maxSm} {
    padding: 1rem;
  }
`;

export const StyledChartTitle = styled.h3`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
`;

export const StyledInsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const StyledInsightCard = styled(motion.div)`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const StyledInsightText = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

export const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  gap: 1rem;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const StyledLoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const StyledLoadingText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
`;

export const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  gap: 1rem;
  text-align: center;
`;

export const StyledErrorText = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 1rem;
`;
